import React from "react";
import { UncontrolledTooltip, Badge } from "reactstrap";
import { Tooltip } from "react-tooltip";
import CustomSelect from "components/customs/dropdown";
import { getFormattedDateString, hasPermission, Toast } from "utility/helpers";
import {
  agencyWithSpecialForms,
  SUPER_ADMIN_ACCOUNTS,
  ACCOUNT_AGENCY,
  canEditRoles,
  ACCOUNT_HOUSE_SPEAKER,
} from "utility/constants";
import {
  AddCommentBtn,
  CheckboxContainer,
  CommentsContainer,
  StyledBumpIcon,
  StyledEditIcon,
  StyledEyeIcon,
  StyledResendIcon,
  StyledTrashIcon,
  ViewCommentBtn,
} from "assets/js/Styles";
import {
  ALLOCATION_STATUS,
  ALLOCATION_STATUS_OPTIONS,
  ALLOWED_STATUS_TO_BUMP_ALLOCATION,
} from "constants/status";
import { regionSelectStyles, statusSelectStyles } from "constants/style";
import CustomCheckbox from "components/customs/checkbox";
import { faCommentDollar } from "@fortawesome/free-solid-svg-icons";
import styled from "styled-components";
import {
  canEditRow,
  cannotEditStatus,
  getStatusOptions,
} from "views/representatives/congressman/utility";
import { Check } from "react-feather";
import { isSelectAllCheckboxShown } from "views/projects/utility";
import { EDIT_ACTION, VIEW_ACTION } from "constants/actions";
import { resendIconStatus } from "../../constants";

const OtherInfoField = styled.p`
  margin: 0;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 3; /* Change this value to the desired number of lines */
  overflow: hidden;
  text-overflow: ellipsis;
  max-height: 4.5em;
  width: 100%;
  white-space: pre-line;
`;

const StyledTooltip = styled(Tooltip)`
  background-color: black;
  max-width: 500px;
  z-index: 1000;
  opacity: 1;
`;

const StyledCustomSelect = styled(CustomSelect)`
  width: 100%;

  .select__control:has(.pending-status) {
    background-color: #00316d;
    border: none;
    .pending-status {
      color: white;
    }
  }

  .select__control:has(.requested-status) {
    background-color: #f3c61c;
    border: none;
    .requested-status {
      color: black;
    }
  }

  .select__control:has(.processed-status) {
    background-color: #265ea3;
    border: none;
    .processed-status {
      color: white;
    }
  }

  .select__control:has(.in-progress-status) {
    background-color: #ffbd14;
    border: none;
    .in-progress-status {
      color: black;
    }
  }

  .select__control:has(.approved-status) {
    background-color: #53a24c;
    border: none;
    .approved-status {
      color: white;
    }
  }

  .select__control:has(.disapproved-status) {
    background-color: #df3348;
    border: none;
    .disapproved-status {
      color: white;
    }
  }

  .select__control:has(.downloaded-status) {
    background-color: #f26e01;
    border: none;
    .downloaded-status {
      color: white;
    }
  }

  .select__control:has(.resubmission-status) {
    background-color: #ff4433;
    border: none;
    .resubmission-status {
      color: white;
    }
  }
`;

const StyledBadge = styled(Badge)`
  background: ${(props) =>
    props.status === ALLOCATION_STATUS.PENDING
      ? "#4252c9"
      : "#f3c61c"} !important;

  width: 9px !important;
  height: 12px !important;

  div {
    opacity: 0;
  }
`;

const isPendingOrRequested = (status) =>
  status === ALLOCATION_STATUS.PENDING ||
  status === ALLOCATION_STATUS.REQUESTED;

const fundSourceColumn = {
  name: "Fund Source",
  selector: (row) => row?.fundSource,
  identifier: "fundSource",
  sortable: true,
  minWidth: "150px",
  cell: ({ status, fundSource }) => {
    return (
      <div
        className={`${isPendingOrRequested(status) ? "font-weight-bold" : ""}`}
      >
        {fundSource}
      </div>
    );
  },
};

const editorNotesColumn = {
  name: "Editor Notes",
  selector: (row) => row?.editorNotes,
  identifier: "editorNotes",
  sortable: true,
  minWidth: "150px",
  cell: ({ status, editorNotes }) =>
    editorNotes ? (
      <div
        className={`${isPendingOrRequested(status) ? "font-weight-bold" : ""}`}
      >
        {editorNotes
          ? editorNotes
              ?.split(";")
              ?.map((notes) => notes)
              ?.join(", ")
          : ""}
      </div>
    ) : (
      <div
        className={`${isPendingOrRequested(status) ? "font-weight-bold" : ""}`}
      >
        {"-"}
      </div>
    ),
};

export const ProgramTable = ({
  id,
  checkedItems,
  tableOption,
  selectedStatus,
  setSelectedStatus,
  resendData,
  setResendData,
  setCheckedItems,
  setResendType,
  role,
  setTableOption,
  isEllipsisUsed,
  handleStatusChange,
  regions,
  setEditingCongressman,
  fetchComments,
  toggleModal,
  timer,
  navigate,
  programID,
  selectedProject,
  setIsResendModalShown,
  setMessageType,
  handleBumpIconOnClicked,
  handleDeleteAction,
}) => {
  const isExecutive = canEditRoles.includes(role);

  const columns = [
    {
      name: "",
      selector: null,
      sortable: false,
      id: "highlight",
      width: "5px",
      cell: () => null,
    },
    {
      name: isSelectAllCheckboxShown(role, checkedItems, tableOption.data) && (
        <CheckboxContainer>
          <CustomCheckbox
            icon={<Check size={16} />}
            checked={checkedItems.length === tableOption.data.length}
            onChange={(evt) => {
              const { checked } = evt.target;

              if (!checked) {
                setCheckedItems([]);
              }
            }}
          />
        </CheckboxContainer>
      ),
      selector: (row) => row?.id,
      sortable: false,
      id: isExecutive ? "checkbox" : "checkbox-hidden",
      width: "60px",
      style: { paddingLeft: 0, paddingRight: 0 },
      cell: (row) => {
        return isExecutive ? (
          <CheckboxContainer>
            <CustomCheckbox
              icon={<Check size={16} />}
              onChange={(evt) => {
                const { checked } = evt.target;

                if (checked) {
                  if (selectedStatus !== "" && row.status !== selectedStatus) {
                    const newItems = checkedItems.filter(
                      (item) => item !== row.id
                    );
                    setCheckedItems(newItems);

                    return Toast({
                      type: 4,
                      content: "All selected rows must have the same status",
                    });
                  }
                  setCheckedItems([...checkedItems, row.id]);
                  setSelectedStatus(row.status);
                  setResendData([...resendData, row]);
                  setResendType("");
                } else {
                  const newItems = checkedItems.filter(
                    (item) => item !== row.id
                  );

                  const data = resendData.filter(
                    (__data) => __data.id !== row.id
                  );
                  setCheckedItems(newItems);
                  if (newItems.length === 0) setSelectedStatus("");
                  setResendData(data);
                }

                return null;
              }}
              checked={checkedItems.includes(row.id)}
            />
          </CheckboxContainer>
        ) : null;
      },
    },
    {
      name: "",
      selector: (row) => row?.badge,
      sortable: false,
      width: "20px",
      id: "badge",
      style: SUPER_ADMIN_ACCOUNTS.includes(role)
        ? { paddingLeft: 0, paddingRight: 0 }
        : null,
      cell: ({ status }, index) => {
        if (isPendingOrRequested(status) && role !== ACCOUNT_HOUSE_SPEAKER) {
          return (
            <StyledBadge
              status={status}
              pill
            >
              <div>{index}</div>
            </StyledBadge>
          );
        }

        return null;
      },
    },
    {
      name: "ID",
      selector: (row) => row?.id,
      identifier: "id",
      sortable: true,
      minWidth: "max-content",
      center: true,
      style: { paddingLeft: 0, paddingRight: 0 },
      cell: (row) => (
        <div
          className={`${
            isPendingOrRequested(row.status) ? "font-weight-bold" : ""
          }`}
        >
          {row.id}
        </div>
      ),
    },
    {
      name: "Recipient",
      selector: (row) => row?.recipient,
      identifier: "recipient",
      grow: 2,
      sortable: true,
      minWidth: "180px",
      cell: ({ status, recipient }) => (
        <div
          className={`${
            isPendingOrRequested(status) ? "font-weight-bold" : ""
          }`}
        >
          {`${recipient?.lastName}, ${recipient?.firstName}`}
        </div>
      ),
    },
    {
      name: "Recommended Amount",
      selector: (row) => row?.budget_allocation,
      identifier: "budget_allocation",
      grow: 2.1,
      sortable: true,
      minWidth: "150px",
      cell: (row) => (
        <div
          className={`${
            isPendingOrRequested(row.status) ? "font-weight-bold" : ""
          }`}
        >
          {`PHP ${Number(row.budget_allocation)?.toLocaleString("en-US")}`}
        </div>
      ),
    },
    {
      name: "Type",
      selector: (row) => row?.allocationType,
      identifier: "allocationType",
      sortable: true,
      minWidth: "100px",
      cell: ({ status, allocationType }) => {
        return (
          <div
            className={`${
              isPendingOrRequested(status) ? "font-weight-bold" : ""
            }`}
          >
            {allocationType?.name}
          </div>
        );
      },
    },
    {
      name: "Other Info",
      selector: (row) => row?.allocationType,
      identifier: "allocationType",
      sortable: true,
      minWidth: "170px",
      grow: 1,
      cell: (
        {
          status,
          allocationName,
          allocationType,
          allocationContactName,
          institution,
          otherInfo,
        },
        index
      ) => {
        let renderOtherInfo = null;

        if (
          Number(id) === 3 &&
          (allocationType?.id === 1 || allocationType?.id === 4)
        ) {
          renderOtherInfo = otherInfo || "";
        } else if (
          agencyWithSpecialForms.includes(Number(id)) &&
          (allocationType?.id === 1 || allocationType?.id === 4)
        ) {
          renderOtherInfo = institution?.name;
        } else if (allocationType.id === 2) {
          renderOtherInfo = allocationName;
        } else if (allocationType?.id === 3) {
          renderOtherInfo = allocationContactName || "";
        }

        const { isEllipsisActive, textRef } = isEllipsisUsed();

        return renderOtherInfo ? (
          <div>
            <OtherInfoField
              className={`${isEllipsisActive ? `truncated-${index}` : ""} ${
                isPendingOrRequested(status) ? "font-weight-bold" : ""
              }`}
              ref={textRef}
              data-tip={isEllipsisActive ? renderOtherInfo : ""}
              data-for={`tooltip-${index}`}
            >
              {renderOtherInfo}
            </OtherInfoField>
            {isEllipsisActive && (
              <StyledTooltip
                id={`tooltip-${index}`}
                effect="float"
                place="top"
                anchorSelect={`.truncated-${index}`}
              >
                {renderOtherInfo}
              </StyledTooltip>
            )}
          </div>
        ) : (
          <div
            className={`${
              isPendingOrRequested(status) ? "font-weight-bold" : ""
            }`}
          >
            {"-"}
          </div>
        );
      },
    },
    {
      name: "Status",
      selector: (row) => row?.status,
      identifier: "status",
      grow: 2,
      sortable: true,
      minWidth: "180px",
      cell: ({ status }, index) => (
        <StyledCustomSelect
          className={`${
            isPendingOrRequested(status) ? "font-weight-bold" : ""
          } ${cannotEditStatus(status, role) ? "" : "pointer"}`}
          isSearchable={false}
          isDisabled={
            cannotEditStatus(status, role) || checkedItems.length !== 0
          }
          onChange={(option) => {
            handleStatusChange(index, option);
          }}
          value={ALLOCATION_STATUS_OPTIONS.find(
            (stat) => stat.value === status
          )}
          options={getStatusOptions(status, role)}
          styles={statusSelectStyles}
          getOptionLabel={(option) => {
            return option?.displayLabel;
          }}
          menuPortalTarget={document.body}
        />
      ),
    },
    {
      name: "Recommended Implementing Agency",
      selector: (row) => row?.implementing,
      identifier: "implementing",
      grow: 2,
      sortable: false,
      minWidth: "165px",
      cell: (row, index) => {
        return (
          <StyledCustomSelect
            className={`${
              isPendingOrRequested(row.status) ? "font-weight-bold" : ""
            }`}
            isSearchable
            isDisabled={!hasPermission("recipient-update")}
            onChange={(option) => {
              const newData = [...tableOption.data];

              if (option.value === 19) {
                newData[index] = {
                  ...row,
                  receivingEntity: option?.label,
                  implementing: {
                    id: 19,
                    region: "Others",
                  },
                };
              } else {
                newData[index] = {
                  ...row,
                  implementing: {
                    id: option?.value,
                    region: option?.label,
                  },
                };
              }

              setTableOption({
                ...tableOption,
                data: newData,
              });
            }}
            value={regions.find(
              (region) => region.value === row.implementing?.id
            )}
            options={regions}
            styles={regionSelectStyles}
            maxMenuHeight="150px"
            menuPortalTarget={document.body}
          />
        );
      },
    },
    {
      name: "Province",
      selector: (row) => row?.province,
      identifier: "province",
      id: "province",
      sortable: true,
      minWidth: "170px",
      cell: ({ status, province }) =>
        province ? (
          <div
            className={`${
              isPendingOrRequested(status) ? "font-weight-bold" : ""
            }`}
          >
            {province?.name}
          </div>
        ) : (
          <div
            className={`${
              isPendingOrRequested(status) ? "font-weight-bold" : ""
            }`}
          >
            {"-"}
          </div>
        ),
    },
    {
      name: "Municipality",
      selector: (row) => row?.municipality,
      identifier: "municipality",
      id: "municipality",
      sortable: true,
      minWidth: "170px",
      cell: ({ status, municipality }) =>
        municipality ? (
          <div
            className={`${
              isPendingOrRequested(status) ? "font-weight-bold" : ""
            }`}
          >
            {municipality?.name}
          </div>
        ) : (
          <div
            className={`${
              isPendingOrRequested(status) ? "font-weight-bold" : ""
            }`}
          >
            {"-"}
          </div>
        ),
    },
    {
      name: "Date Requested",
      selector: (row) => row?.dateRequested,
      identifier: "dateRequested",
      id: "dateRequested",
      sortable: true,
      minWidth: "150px",
      cell: ({ status, dateRequested }) => (
        <div
          className={`${
            isPendingOrRequested(status) ? "font-weight-bold" : ""
          }`}
        >
          {getFormattedDateString(dateRequested, "MMM-dd-yyy")}
        </div>
      ),
    },
    {
      name: "Date Endorsed",
      selector: (row) => row?.dateApproved,
      identifier: "dateApproved",
      id: "dateApproved",
      sortable: true,
      minWidth: "150px",
      cell: ({ dateApproved, status }) =>
        dateApproved ? (
          <div
            className={`${
              isPendingOrRequested(status) ? "font-weight-bold" : ""
            }`}
          >
            {getFormattedDateString(dateApproved, "MMM-dd-yyyy")}
          </div>
        ) : (
          <div
            className={`${
              isPendingOrRequested(status) ? "font-weight-bold" : ""
            }`}
          >
            {"-"}
          </div>
        ),
    },
    {
      name: "Remarks",
      selector: (row) => row?.hasComments,
      sortable: false,
      minWidth: "160px",
      cell: (row) => (
        <CommentsContainer>
          {row.hasComments === 1 && hasPermission("comment-view") && (
            <ViewCommentBtn
              color="primary"
              size="sm"
              onClick={() => {
                setEditingCongressman(row.id);
                fetchComments(row.id);
                toggleModal();
              }}
            >
              {"View Comment"}
            </ViewCommentBtn>
          )}

          {row.hasComments === 0 && hasPermission("comment-create") && (
            <AddCommentBtn
              size="sm"
              onClick={() => {
                setEditingCongressman(row.id);
                fetchComments(row.id);
                toggleModal();
              }}
            >
              {"Add Comment"}
            </AddCommentBtn>
          )}
        </CommentsContainer>
      ),
    },
    {
      name: "Updated By",
      selector: (row) => row?.updatedBy,
      identifier: "updatedBy",
      id: "updatedBy",
      grow: 1,
      sortable: true,
      minWidth: "150px",
      cell: ({ updatedBy, status }) =>
        updatedBy && updatedBy !== null ? (
          <div
            className={`${
              isPendingOrRequested(status) ? "font-weight-bold" : ""
            }`}
          >
            {updatedBy}
          </div>
        ) : (
          <div
            className={`${
              isPendingOrRequested(status) ? "font-weight-bold" : ""
            }`}
          >
            {"-"}
          </div>
        ),
    },
    {
      name: "Actions",
      selector: (row) => row?.id,
      sortable: false,
      width: "150px",
      cell: (row) => {
        const isBumpIconDisabled =
          timer && timer?.find((time) => time?.id === row.id && time?.isActive);

        return (
          <>
            {canEditRow(row?.status, role) && (
              <>
                <StyledEditIcon
                  size={16}
                  id={`edit-row--${row.id}`}
                  onClick={() => {
                    navigate(`/projects/${id}/program/${programID}/recipient`, {
                      state: {
                        row,
                        agency: id,
                        program: selectedProject?.ID,
                        action: EDIT_ACTION,
                      },
                    });
                  }}
                />
                <UncontrolledTooltip
                  placement="left"
                  target={`edit-row--${row.id}`}
                >
                  {"Edit"}
                </UncontrolledTooltip>
              </>
            )}

            <StyledEyeIcon
              size={16}
              onClick={() =>
                navigate(`/projects/${id}/program/${programID}/recipient`, {
                  state: {
                    row,
                    agency: id,
                    program: selectedProject?.ID,
                    action: VIEW_ACTION,
                  },
                })
              }
              id={`view-row--${row.id}`}
            />
            <UncontrolledTooltip
              placement="top"
              target={`view-row--${row.id}`}
            >
              {"View"}
            </UncontrolledTooltip>

            {resendIconStatus.includes(row.status) && isExecutive && (
              <>
                <StyledResendIcon
                  size={16}
                  id={`resend-row--${row.id}`}
                  onClick={() => {
                    setIsResendModalShown(true);
                    setResendData([row]);
                    setResendType("single");
                    setMessageType("approval");
                  }}
                />
                <UncontrolledTooltip
                  placement="top"
                  target={`resend-row--${row.id}`}
                >
                  {"Resend"}
                </UncontrolledTooltip>
              </>
            )}

            {ALLOWED_STATUS_TO_BUMP_ALLOCATION.includes(row.status) && (
              <>
                <StyledBumpIcon
                  id={`bump-row--${row.id}`}
                  icon={faCommentDollar}
                  size="xl"
                  style={
                    !isBumpIconDisabled
                      ? { cursor: "pointer" }
                      : { cursor: "initial", pointerEvents: "none" }
                  }
                  disabled={isBumpIconDisabled}
                  onClick={() => handleBumpIconOnClicked(row.id, row.status)}
                />

                <UncontrolledTooltip
                  placement="top"
                  target={`bump-row--${row.id}`}
                >
                  {"Bump"}
                </UncontrolledTooltip>
              </>
            )}

            {hasPermission("recipient-delete") && (
              <>
                <StyledTrashIcon
                  size={16}
                  id={`delete-row--${row.id}`}
                  onClick={() => handleDeleteAction(row)}
                />
                <UncontrolledTooltip
                  placement="right"
                  target={`delete-row--${row.id}`}
                >
                  {"Delete"}
                </UncontrolledTooltip>
              </>
            )}
          </>
        );
      },
    },
  ];

  if (
    (Number(id) === 14 || (Number(id) === 3 && selectedProject?.ID === 14)) &&
    role &&
    (isExecutive || role === ACCOUNT_AGENCY)
  ) {
    columns.splice(10, 0, fundSourceColumn);
  }

  if (role && isExecutive) {
    columns.splice(12, 0, editorNotesColumn);
  }

  return columns;
};
